import { ErrorBoundary } from '@sleepstation/components'
import GlobalStyles from '@sleepstation/styles-global'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'
import { css } from 'styled-components'
import Providers from './view/components/Providers'
import RootLayoutModule from './view/components/Site/RootLayoutModule'
import TopLevelErrorNotification from './view/components/shared/errorNotifications/TopLevelErrorNotification'
import ErrorPageModule from './view/components/templates/ErrorPageModule'
import PortalCouldYouHaveInsomniaModule from './view/components/templates/PortalCouldYouHaveInsomniaModule'
import PortalDashboardModule from './view/components/templates/PortalDashboardModule'
import PortalExpertGuidanceModule from './view/components/templates/PortalExpertGuidanceModule'
import PortalKnowledgeBaseModule from './view/components/templates/PortalKnowledgeBaseModule'
import PortalLandingModule from './view/components/templates/PortalLandingModule'
import PortalPrivacyModule from './view/components/templates/PortalPrivacyModule'
import PortalQuizAreYouGettingEnoughSleepQuizIntroModule from './view/components/templates/PortalQuizAreYouGettingEnoughSleepQuizIntroModule'
import PortalQuizAreYouGettingEnoughSleepQuizQuestionsModule from './view/components/templates/PortalQuizAreYouGettingEnoughSleepQuizQuestionsModule'
import PortalQuizAreYouGettingEnoughSleepQuizResultsModule from './view/components/templates/PortalQuizAreYouGettingEnoughSleepQuizResultsModule'
import PortalQuizWhatDoYouKnowAboutSleepQuizIntroModule from './view/components/templates/PortalQuizWhatDoYouKnowAboutSleepQuizIntroModule'
import PortalQuizWhatDoYouKnowAboutSleepQuizQuestionsModule from './view/components/templates/PortalQuizWhatDoYouKnowAboutSleepQuizQuestionsModule'
import PortalQuizWhatDoYouKnowAboutSleepQuizResultsModule from './view/components/templates/PortalQuizWhatDoYouKnowAboutSleepQuizResultsModule'
import PortalTermsModule from './view/components/templates/PortalTermsModule'
import TheInformationStandardModule from './view/components/templates/TheInformationStandardModule'
import initialiseErrorReporter from './view/init/initializeErrorReporter'
import { isDevelopmentBuild, isTestBuild } from './view/utils/environment'
import { getPageDataForRequest } from './view/utils/routing'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const APP_ELEMENT_ID = `___client`

const ADDITIONAL_STYLES = css`
  html {
    scroll-behavior: smooth;
  }
`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const loader = async (props) => getPageDataForRequest(props)

// -----------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------

const ROUTER = createBrowserRouter([
  {
    element: <RootLayoutModule />,
    errorElement: (
      <RootLayoutModule>
        <ErrorPageModule />
      </RootLayoutModule>
    ),
    children: [
      {
        path: `/:portalNamespace/portal/the-information-standard`,
        element: <TheInformationStandardModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/`,
        element: <PortalLandingModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/dashboard`,
        element: <PortalDashboardModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/knowledge-base/`,
        element: <PortalKnowledgeBaseModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/could-you-have-insomnia/`,
        element: <PortalCouldYouHaveInsomniaModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/expert-guidance/`,
        element: <PortalExpertGuidanceModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/are-you-getting-enough-sleep/intro/`,
        element: <PortalQuizAreYouGettingEnoughSleepQuizIntroModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/are-you-getting-enough-sleep/questions/`,
        element: <PortalQuizAreYouGettingEnoughSleepQuizQuestionsModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/are-you-getting-enough-sleep/results/`,
        element: <PortalQuizAreYouGettingEnoughSleepQuizResultsModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/what-do-you-know-about-sleep/intro/`,
        element: <PortalQuizWhatDoYouKnowAboutSleepQuizIntroModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/what-do-you-know-about-sleep/questions/`,
        element: <PortalQuizWhatDoYouKnowAboutSleepQuizQuestionsModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/what-do-you-know-about-sleep/results/`,
        element: <PortalQuizWhatDoYouKnowAboutSleepQuizResultsModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/privacy/`,
        element: <PortalPrivacyModule />,
        loader,
      },
      {
        path: `/:portalNamespace/portal/terms/`,
        element: <PortalTermsModule />,
        loader,
      },
    ],
  },
])

// -----------------------------------------------------------------------------
// Render App Component
//
// This is the app entry-point.
// -----------------------------------------------------------------------------

// eslint-disable-next-line import/prefer-default-export
const initApp = ({ appElementId } = {}) => {
  // Initialise Sentry as early as possible
  // Note that we don't need error reporting in development or under test, only
  // on remote servers.
  const shouldEnableErrorReporter =
    true || (!isDevelopmentBuild() && !isTestBuild())

  initialiseErrorReporter({ isEnabled: shouldEnableErrorReporter })

  const domNode = document.getElementById(appElementId)
  const root = createRoot(domNode)

  root.render(
    <React.StrictMode>
      <Providers appElementId={appElementId}>
        <ErrorBoundary Fallback={TopLevelErrorNotification}>
          <GlobalStyles additionalStyles={ADDITIONAL_STYLES} />
          <RouterProvider router={ROUTER} />
        </ErrorBoundary>
      </Providers>
    </React.StrictMode>
  )
}

initApp({
  appElementId: APP_ELEMENT_ID,
})
